/**
 * =============================================================================
 * Sectores custom del World Builder ("biomas" hechos a mano)
 * =============================================================================
 *
 * Un sector es una región con particularidades propias que definimos EN CÓDIGO
 * (anillos de árboles, entradas, biomas custom, etc.). El editor solo lo
 * "estampa" en una posición (ver WorldSectorPlacement en worldBlueprint.ts) y el
 * generador (`worldGenerator.ts`) llama a `generate()` de cada sector colocado.
 *
 * Mayormente se usan una vez por mapa, pero al vivir en el builder el mundo se
 * puede re-generar siempre igual (determinismo: todo lo aleatorio sale de
 * `createRng(hash2(...))`, nunca `Math.random`/`Date.now`).
 *
 * Para AGREGAR un sector nuevo: definir un `SectorDefinition` y sumarlo a
 * `SECTORS`. Aparece automáticamente en el selector del World Builder.
 *
 * Ver docs/WORLD_BUILDER.md → "Sectores custom".
 */

import type {
  TileData,
  MapTransladoData,
  MapParticleData,
  MapLightData,
  MapAreaData,
  MapRect,
} from '../types/map';
import { createRng, hash2 } from './prng';
import { path9Sprite } from './pathSprites';
import {
  HOUSES,
  HOUSE_IDS,
  houseWidthTiles,
  houseVisualDepthTiles,
  houseFootprintDepthTiles,
  stampHouse,
} from './houseData';

/**
 * Lado del bloque de superficie en tiles. DEBE coincidir con `BLOCK` de
 * `worldGenerator.ts`: el camino del builder se genera a nivel de BLOQUE (cada bloque
 * de camino = 4×4 tiles, centrado en su bloque). Las plazoletas/caminos de los sectores
 * se **snapean al centro del bloque** (`snapToBlock`) para quedar sobre la misma grilla
 * y empalmar sin el "escalón" que aparecía cuando la conexión (a nivel de tile, en el
 * centro de la celda) no caía sobre el centro del bloque del camino (notorio con
 * `cellSize` no múltiplo de 4, ej. 35).
 */
const PATH_BLOCK = 4;
/** Snapea una coord de tile al CENTRO de su bloque 4×4 (alineado con el camino del builder). */
export function snapToBlock(v: number): number {
  return Math.floor(v / PATH_BLOCK) * PATH_BLOCK + Math.floor(PATH_BLOCK / 2);
}

/** Contexto que recibe un sector para generarse (en coordenadas de tile). */
export interface SectorContext {
  /** Centro del sector en tiles. */
  centerTileX: number;
  centerTileY: number;
  /** Radio del sector en tiles. */
  radiusTiles: number;
  /** Seed determinista (combina bp.seed con la posición). */
  seed: number;
  /** Catálogo de árboles disponible (ids de assets-index, type tree). */
  treeIds: string[];
  /** Catálogo de entradas de dungeon disponible (type dungeon_entrance). */
  entranceIds: string[];
  /** True si (tx,ty) es un tile de camino del builder (para no taparlo con árboles). */
  isPath?: (tx: number, ty: number) => boolean;
  /**
   * True si (tx,ty) cae sobre AGUA (bioma del bloque). Lo usa la conexión de adoquín para NO
   * tender caminos sobre el agua ni conectar a un camino del otro lado de un lago.
   */
  isWater?: (tx: number, ty: number) => boolean;
  /** Id numérico del mapa generado (para translados same-map). */
  mapId: number;
}

/** Salida de un sector: fragmentos de MapData que el generador concatena. */
export interface SectorOutput {
  /** Decoraciones (árboles, entradas, cuerpos de casa) → capa `ground_deco_over`. */
  decoTiles?: TileData[];
  /** Techos de casa → capa `roof` (depth fijo, SIEMPRE encima del personaje). */
  roofTiles?: TileData[];
  /** Tiles de colisión (huella de edificios) → capa `_collision` (no se renderiza). */
  blockedTiles?: TileData[];
  /** Translados (ej. entradas de dungeon). */
  translados?: MapTransladoData[];
  /** Override de suelo (para biomas custom). Capa `layer1_ground`. */
  groundTiles?: TileData[];
  /** Override de costa. Capa `layer2_floor_objects`. */
  coastTiles?: TileData[];
  /**
   * Bloques de tierra (`pattern6`, `_4x4`) → capa `layer2_floor_objects`, DEBAJO del adoquín.
   * Sendero puerta→camino de las casas de ciudad (ver `makeCitySector`).
   */
  dirtTiles?: TileData[];
  /** Luces / fuentes de oscuridad (MapData.lights). `dark:true` = sombra suave. */
  lights?: MapLightData[];
  /** Emisores de partículas ambiente (cosmético). */
  particles?: MapParticleData[];
  /** Tiles "x,y" donde el generador NO debe esparcir vegetación genérica. */
  suppressTiles?: Set<string>;
  /** Áreas a sumar al mapa (ej. interior de casa, efecto de oscurecer al entrar). */
  areas?: MapAreaData[];
}

/** Definición de un sector custom. */
export interface SectorDefinition {
  id: string;
  name: string;
  /** Radio por defecto en tiles (el editor lo puede ajustar). */
  defaultRadiusTiles: number;
  generate(ctx: SectorContext): SectorOutput;
}

// ============================================================================
// Sector "prueba": anillo exterior de un árbol, anillo interior de otro, y una
// entrada de dungeon al centro. Constantes fáciles de cambiar.
// ============================================================================

/** Árbol del anillo exterior (distancia ∈ [0.6R, R]). */
const PRUEBA_OUTER_TREE = 'tree5';
/** Árbol del anillo interior (distancia ∈ [0.3R, 0.6R]). */
const PRUEBA_INNER_TREE = 'tree11';
/** Entrada de dungeon. */
const PRUEBA_ENTRANCE = 'entrance28';
/** Columna (offset X respecto del centro) donde va la entrada y baja el camino. */
const PRUEBA_ENTRANCE_DX = 2;
/** Destino del translado: N tiles hacia abajo de la entrada (mismo mapa, placeholder). */
const PRUEBA_DUNGEON_DROP = 10;
/** Partícula ambiente sobre la entrada (efecto loop de shared/data/particleEffects). */
const PRUEBA_ENTRANCE_FX = 'fx_dark_curse_aura';
/** Opacidad máxima (centro) de la sombra de tenebrosidad del interior (0..1). */
const PRUEBA_GLOOM_INTENSITY = 0.5;
/** Tamaño de tile en px (para convertir el radio de la sombra). */
const TILE_PX = 32;
/** Densidad de árboles por tile dentro de cada anillo (0..1). */
const PRUEBA_RING_DENSITY = 0.05;
/**
 * Ancho del corredor libre (camino) en tiles: banda horizontal central + columna
 * hacia abajo desde el centro. 6 tiles de ancho (offsets -3..2 respecto del centro).
 */
const PRUEBA_CLEAR_LO = -3;
const PRUEBA_CLEAR_HI = 2;

const pruebaSector: SectorDefinition = {
  id: 'prueba',
  name: 'Prueba',
  defaultRadiusTiles: 24,
  generate(ctx: SectorContext): SectorOutput {
    const { centerTileX: cx, centerTileY: cy, radiusTiles: R, seed } = ctx;
    const decoTiles: TileData[] = [];
    const suppressTiles = new Set<string>();
    const rng = createRng(hash2(cx, cy, seed));

    const innerLo = R * 0.3;
    const innerHi = R * 0.6;
    const outerLo = R * 0.6;
    const outerHi = R;

    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > R) continue;
        const tx = cx + dx;
        const ty = cy + dy;
        if (tx < 0 || ty < 0) continue;
        const key = `${tx},${ty}`;
        // Todo el círculo suprime la vegetación genérica de las celdas.
        suppressTiles.add(key);

        // Nunca tapar el camino dibujado con el builder (path tiles).
        if (ctx.isPath?.(tx, ty)) continue;

        // Corredor libre (camino): banda horizontal central + columna hacia abajo
        // alineada bajo la entrada (6 tiles de ancho). Sin árboles ni decoración.
        const inHorizontalBand = dy >= PRUEBA_CLEAR_LO && dy <= PRUEBA_CLEAR_HI;
        const inDownColumn =
          dx - PRUEBA_ENTRANCE_DX >= PRUEBA_CLEAR_LO &&
          dx - PRUEBA_ENTRANCE_DX <= PRUEBA_CLEAR_HI &&
          dy >= 0;
        if (inHorizontalBand || inDownColumn) continue;

        let species: string | null = null;
        if (dist >= outerLo && dist <= outerHi) species = PRUEBA_OUTER_TREE;
        else if (dist >= innerLo && dist < innerHi) species = PRUEBA_INNER_TREE;
        if (!species) continue;

        if (rng.next() < PRUEBA_RING_DENSITY) {
          decoTiles.push({ x: tx, y: ty, sprite: species, blocked: true });
        }
      }
    }

    // Entrada de dungeon (deco que ocluye como árbol). NO se bloquea: el player
    // puede pisarla y el translado se dispara al pisar ese mismo tile.
    const entranceSprite = ctx.entranceIds.includes(PRUEBA_ENTRANCE)
      ? PRUEBA_ENTRANCE
      : ctx.entranceIds[0] ?? PRUEBA_ENTRANCE;
    const entranceX = cx + PRUEBA_ENTRANCE_DX;
    decoTiles.push({ x: entranceX, y: cy, sprite: entranceSprite });
    // Translado MISMO MAPA (placeholder hasta cablear el dungeon real): lleva
    // 10 tiles hacia abajo de la entrada. (destMapId:0 rompía: no existe el mapa 0.)
    const translados: MapTransladoData[] = [
      {
        id: `sector_prueba_${cx}_${cy}`,
        x: entranceX,
        y: cy,
        destMapId: ctx.mapId,
        destX: entranceX,
        destY: cy + PRUEBA_DUNGEON_DROP,
      },
    ];

    // Partícula ambiente sobre la entrada (cosmético).
    const particles: MapParticleData[] = [
      { id: `sector_prueba_fx_${cx}_${cy}`, x: entranceX, y: cy, fxId: PRUEBA_ENTRANCE_FX },
    ];

    // Tenebrosidad: fuente de OSCURIDAD (sombra suave) centrada en el sector,
    // vía el sistema de luces → bordes difuminados (no el "cuadriculado" de tiles).
    // El radio cubre el disco interior (hasta el inner ring). `radius` en PÍXELES.
    const lights: MapLightData[] = [
      {
        id: `sector_prueba_gloom_${cx}_${cy}`,
        x: cx,
        y: cy,
        radius: Math.round(innerHi * TILE_PX),
        intensity: PRUEBA_GLOOM_INTENSITY,
        color: '0x05060f',
        dark: true,
      },
    ];

    return { decoTiles, translados, particles, lights, suppressTiles };
  },
};

// ============================================================================
// Sector "Entrada Catacumbas": coloca SIEMPRE la misma entrada de dungeon en el
// centro, sobre una plazoleta de adoquín, y —si el builder tiene camino marcado—
// tira un camino de adoquín que conecta la entrada con el tile de camino más
// cercano. Si no hay camino marcado, solo deja la entrada con un claro alrededor.
// Constantes fáciles de cambiar arriba del sector.
// ============================================================================

/** Radio (tiles) del claro alrededor de la entrada: sin vegetación genérica. */
const DGENTRY_CLEAR_RADIUS = 4;
/** Radio (tiles) del disco de adoquín bajo la entrada (la plazoleta). */
const DGENTRY_PLAZA_RADIUS = 2;
/** Semi-ancho del camino de conexión: 2 → camino de 5 tiles de ancho (igual que el builder). */
const DGENTRY_ROAD_HALF_WIDTH = 2;
/**
 * Distancia máxima (tiles) a la que se busca el camino del builder para conectar.
 * ~1 celda (35): si hay un camino pegado a la celda de la entrada lo puentea con un
 * solo ramal; si el camino más cercano está más lejos, la entrada queda suelta (deco).
 */
const DGENTRY_MAX_CONNECT = 40;
/** Destino del translado: N tiles hacia abajo (mismo mapa, placeholder hasta cablear el dungeon). */
const DGENTRY_DUNGEON_DROP = 10;
/** Opacidad máxima (centro) de la sombra de tenebrosidad sobre la entrada (0..1). */
const DGENTRY_GLOOM_INTENSITY = 0.45;

/**
 * ¿La "L" engrosada horizontal-primero (igual que `stampThickL`) de (x0,y0)→(x1,y1) cruza
 * algún tile bloqueado (agua)? Se usa para NO tender adoquín sobre el agua ni conectar a un
 * camino que está del OTRO lado de un lago/costa. Chequea la centerline (los cuerpos de agua
 * son ≥ 1 bloque de ancho, así que la centerline basta para detectar el cruce).
 */
function lRouteCrossesBlocked(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  isBlocked: (tx: number, ty: number) => boolean
): boolean {
  const stepX = Math.sign(x1 - x0);
  const stepY = Math.sign(y1 - y0);
  if (stepX !== 0) for (let x = x0; x !== x1; x += stepX) if (isBlocked(x, y0)) return true;
  if (stepY !== 0) for (let y = y0; y !== y1; y += stepY) if (isBlocked(x1, y)) return true;
  return isBlocked(x1, y1);
}

/**
 * Busca el tile de camino del builder MÁS CERCANO al centro (un SOLO target). Itera
 * anillos cuadrados crecientes (Chebyshev) y devuelve el tile de camino más cercano
 * dentro de `maxR`, o null. Antes se conectaba a los 8 octantes (un abanico de "L" que
 * armaba caminos paralelos/cajas sin sentido alrededor de entradas/ciudades); ahora se
 * tira UNA conexión sensata: el sector se enlaza a la red por su punto más cercano.
 *
 * `isBlocked` (opcional): si la "L" al candidato cruza un tile bloqueado (agua), se descarta
 * ese candidato → no se conecta a un camino del otro lado de un lago (evita adoquín sobre agua).
 */
export function findNearestPath(
  isPath: (tx: number, ty: number) => boolean,
  cx: number,
  cy: number,
  maxR: number,
  isBlocked?: (tx: number, ty: number) => boolean
): { x: number; y: number } | null {
  for (let r = 1; r <= maxR; r++) {
    let best: { x: number; y: number } | null = null;
    let bestD = Infinity;
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // solo el perímetro del anillo
        const tx = cx + dx;
        const ty = cy + dy;
        if (tx < 0 || ty < 0) continue;
        if (!isPath(tx, ty)) continue;
        if (isBlocked && lRouteCrossesBlocked(cx, cy, snapToBlock(tx), snapToBlock(ty), isBlocked)) {
          continue; // el ramal cruzaría agua → no es una conexión válida
        }
        const d = dx * dx + dy * dy; // dentro del anillo, el de menor dist euclídea
        if (d < bestD) {
          bestD = d;
          best = { x: tx, y: ty };
        }
      }
    }
    if (best) return best; // primer anillo con camino → el más cercano
  }
  return null;
}

/** Direcciones cardinales para la conexión multi-ramal. */
type Cardinal = 'N' | 'S' | 'E' | 'W';

/**
 * Busca el tile de camino del builder MÁS CERCANO en CADA dirección cardinal (hasta 4),
 * para que un sector que cae en una JUNTA de caminos (una ciudad en la esquina de una "L",
 * o un camino pasante) reconecte TODOS sus brazos, no solo uno.
 *
 * Recorre anillos Chebyshev crecientes (determinista, igual que `findNearestPath`) y
 * clasifica cada tile de camino en UNA dirección según su componente dominante
 * (`|dx| >= |dy|` → E/W, si no → N/S). El primer anillo que aporta una dirección la fija
 * (es la más cercana; tie-break por dist. euclídea dentro del anillo). Corta apenas tiene
 * las 4 o se agotan los anillos.
 *
 * La clasificación por bins cardinales evita el problema viejo de las "cajas" (conectar a
 * los 8 octantes armaba abanicos de "L" al mismo camino): un camino recto al este cae SOLO
 * en el bin `E` → un único ramal. `minGap` descarta un target si ya hay otro aceptado a esa
 * distancia (mismo camino contado en dos cardinales, ej. una esquina).
 */
export function findNearestPathPerDirection(
  isPath: (tx: number, ty: number) => boolean,
  cx: number,
  cy: number,
  maxR: number,
  minGap: number,
  isBlocked?: (tx: number, ty: number) => boolean
): { x: number; y: number }[] {
  const best: Record<Cardinal, { x: number; y: number; d: number } | null> = {
    N: null,
    S: null,
    E: null,
    W: null,
  };
  const classify = (dx: number, dy: number): Cardinal =>
    Math.abs(dx) >= Math.abs(dy) ? (dx > 0 ? 'E' : 'W') : dy > 0 ? 'S' : 'N';
  for (let r = 1; r <= maxR; r++) {
    for (let dx = -r; dx <= r; dx++) {
      for (let dy = -r; dy <= r; dy++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue; // solo el perímetro del anillo
        const tx = cx + dx;
        const ty = cy + dy;
        if (tx < 0 || ty < 0) continue;
        if (!isPath(tx, ty)) continue;
        if (isBlocked && lRouteCrossesBlocked(cx, cy, snapToBlock(tx), snapToBlock(ty), isBlocked)) {
          continue; // el ramal a este camino cruzaría agua → descartar (no adoquín sobre agua)
        }
        const dir = classify(dx, dy);
        const d = dx * dx + dy * dy;
        // Solo fijar cada dirección con el PRIMER anillo que la aporta (el más cercano);
        // dentro del anillo, el de menor dist. euclídea.
        if (best[dir] === null || d < (best[dir] as { d: number }).d) {
          best[dir] = { x: tx, y: ty, d };
        }
      }
    }
    if (best.N && best.S && best.E && best.W) break; // las 4 ya fijadas
  }

  // Ordenar por cercanía y dedupear targets casi coincidentes (mismo camino).
  const found = (['E', 'W', 'S', 'N'] as Cardinal[])
    .map((dir) => best[dir])
    .filter((t): t is { x: number; y: number; d: number } => t !== null)
    .sort((a, b) => a.d - b.d);
  const out: { x: number; y: number }[] = [];
  for (const t of found) {
    if (out.some((o) => Math.abs(o.x - t.x) <= minGap && Math.abs(o.y - t.y) <= minGap)) {
      continue;
    }
    out.push({ x: t.x, y: t.y });
  }
  return out;
}

/** Opciones del adoquín de un sector (plazoleta central + camino de conexión). */
interface CobbleOptions {
  /** Radio (tiles) del disco de adoquín central. */
  plazaRadius: number;
  /** Semi-ancho del camino de conexión (1 → 3 tiles de ancho). */
  halfWidth: number;
  /** Distancia máxima (tiles) a la que se busca el camino del builder. */
  maxConnect: number;
  /**
   * Opcional: tiles que el adoquín NO debe pisar ni cruzar (AGUA). Si se pasa, la plazoleta y
   * los ramales saltean el agua y NO se conecta a un camino que esté del otro lado de un lago
   * → evita "camino sobre el agua" y uniones raras cerca de la costa.
   */
  isBlocked?: (tx: number, ty: number) => boolean;
}

/**
 * Agrega un disco de adoquín (plazoleta) al roadSet, centrado en (scx,scy). Con `isBlocked`
 * (agua) saltea esos tiles → la plazoleta no se derrama sobre el agua (que quedaría caminable).
 */
function stampDisk(
  roadSet: Set<string>,
  scx: number,
  scy: number,
  r: number,
  isBlocked?: (tx: number, ty: number) => boolean
): void {
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      if (dx * dx + dy * dy > r * r) continue;
      const tx = scx + dx;
      const ty = scy + dy;
      if (tx < 0 || ty < 0) continue;
      if (isBlocked && isBlocked(tx, ty)) continue;
      roadSet.add(`${tx},${ty}`);
    }
  }
}

/** Estampa un punto engrosado (pincel cuadrado de semi-ancho hw) en el roadSet. Con
 * `isBlocked` (agua) saltea esos tiles → el camino no pisa el agua. */
function stampThick(
  roadSet: Set<string>,
  lx: number,
  ly: number,
  hw: number,
  isBlocked?: (tx: number, ty: number) => boolean
): void {
  for (let oy = -hw; oy <= hw; oy++) {
    for (let ox = -hw; ox <= hw; ox++) {
      const tx = lx + ox;
      const ty = ly + oy;
      if (tx < 0 || ty < 0) continue;
      if (isBlocked && isBlocked(tx, ty)) continue;
      roadSet.add(`${tx},${ty}`);
    }
  }
}

/**
 * Estampa un camino en "L" engrosado del (x0,y0) al (x1,y1) en el roadSet. Por defecto va
 * horizontal y después vertical; con `verticalFirst` va vertical y después horizontal (útil
 * para que una calle salga derecho por el FRENTE de una casa antes de doblar a la plaza).
 * Los extremos deberían venir snapeados al bloque 4×4 para empalmar sin escalón.
 */
function stampThickL(
  roadSet: Set<string>,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  hw: number,
  verticalFirst = false,
  isBlocked?: (tx: number, ty: number) => boolean
): void {
  const stepX = Math.sign(x1 - x0);
  const stepY = Math.sign(y1 - y0);
  if (verticalFirst) {
    for (let y = y0; y !== y1; y += stepY) stampThick(roadSet, x0, y, hw, isBlocked);
    for (let x = x0; x !== x1; x += stepX) stampThick(roadSet, x, y1, hw, isBlocked);
  } else {
    for (let x = x0; x !== x1; x += stepX) stampThick(roadSet, x, y0, hw, isBlocked);
    for (let y = y0; y !== y1; y += stepY) stampThick(roadSet, x1, y, hw, isBlocked);
  }
  stampThick(roadSet, x1, y1, hw, isBlocked);
}

/** Pincel cuadrado ASIMÉTRICO: estampa offsets `lo..hi` en ambos ejes (ancho `hi-lo+1`). */
function stampBox(roadSet: Set<string>, lx: number, ly: number, lo: number, hi: number): void {
  for (let oy = lo; oy <= hi; oy++) {
    for (let ox = lo; ox <= hi; ox++) {
      const tx = lx + ox;
      const ty = ly + oy;
      if (tx < 0 || ty < 0) continue;
      roadSet.add(`${tx},${ty}`);
    }
  }
}

/**
 * "L" engrosada de ancho arbitrario (par o impar) usando el pincel `stampBox` con offsets
 * `lo..hi`. Horizontal-primero. Se usa para las calles internas de la ciudad (ancho 4).
 */
function stampBandL(
  roadSet: Set<string>,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  lo: number,
  hi: number
): void {
  const stepX = Math.sign(x1 - x0);
  const stepY = Math.sign(y1 - y0);
  for (let x = x0; x !== x1; x += stepX) stampBox(roadSet, x, y0, lo, hi);
  for (let y = y0; y !== y1; y += stepY) stampBox(roadSet, x1, y, lo, hi);
  stampBox(roadSet, x1, y1, lo, hi);
}

/**
 * Construye el SET de tiles de adoquín (CRUDO, sin 9-slice) de un sector: una
 * **plazoleta circular** en el centro (snapeado al bloque) + un **ramal en "L" por cada
 * dirección cardinal** con camino del builder cerca (hasta 4, vía
 * `findNearestPathPerDirection`) → reconecta todos los brazos de una junta/paso. Separado
 * de `sliceRoadTiles` a propósito: así un sector (ej. ciudad) puede AGREGAR más tiles
 * (calles internas casa→plaza) al set y resolver el 9-slice de una sola vez, SIN costuras.
 */
export function computeCobbleRoadSet(
  cx: number,
  cy: number,
  isPath: (tx: number, ty: number) => boolean,
  opts: CobbleOptions
): Set<string> {
  const roadSet = new Set<string>();
  // Centro SNAPEADO al centro del bloque 4×4 → cae sobre la misma grilla que el camino
  // del builder (block-aligned) y empalma sin escalón.
  const scx = snapToBlock(cx);
  const scy = snapToBlock(cy);
  stampDisk(roadSet, scx, scy, opts.plazaRadius, opts.isBlocked);
  const targets = findNearestPathPerDirection(
    isPath,
    scx,
    scy,
    opts.maxConnect,
    2 * opts.halfWidth,
    opts.isBlocked
  );
  for (const found of targets) {
    stampThickL(
      roadSet,
      scx,
      scy,
      snapToBlock(found.x),
      snapToBlock(found.y),
      opts.halfWidth,
      false,
      opts.isBlocked
    );
  }
  return roadSet;
}

/**
 * Resuelve el sprite 9-slice de cada tile de un roadSet de adoquín. Un vecino cuenta como
 * camino si está en el set O es camino del builder (`isPath`) → empalme sin costura. No
 * re-emite sobre el camino del builder (evita doble tile en la misma capa).
 */
export function sliceRoadTiles(
  roadSet: Set<string>,
  isPath: (tx: number, ty: number) => boolean
): TileData[] {
  const isRoad = (tx: number, ty: number): boolean =>
    roadSet.has(`${tx},${ty}`) || isPath(tx, ty);
  const coastTiles: TileData[] = [];
  for (const key of roadSet) {
    const [x, y] = key.split(',').map(Number);
    if (isPath(x, y)) continue;
    coastTiles.push({
      x,
      y,
      sprite: path9Sprite(
        isRoad(x, y - 1),
        isRoad(x, y + 1),
        isRoad(x + 1, y),
        isRoad(x - 1, y),
        isRoad(x - 1, y - 1),
        isRoad(x + 1, y - 1),
        isRoad(x - 1, y + 1),
        isRoad(x + 1, y + 1)
      ),
    });
  }
  return coastTiles;
}

/**
 * Arma el adoquín de un sector (plazoleta + ramales por cardinal) y lo 9-slicea. Wrapper
 * de `computeCobbleRoadSet` + `sliceRoadTiles` para los sectores que no agregan calles
 * (entradas de dungeon, entradas genéricas del generador). Devuelve los `coastTiles`
 * listos y el set de tiles ocupados (para suprimir vegetación encima).
 */
export function buildCobbleConnection(
  cx: number,
  cy: number,
  isPath: (tx: number, ty: number) => boolean,
  opts: CobbleOptions
): { coastTiles: TileData[]; tiles: Set<string> } {
  const roadSet = computeCobbleRoadSet(cx, cy, isPath, opts);
  return { coastTiles: sliceRoadTiles(roadSet, isPath), tiles: roadSet };
}

/** Config de un sector de "entrada de dungeon" (catacumbas y cada dungeon real). */
interface DungeonEntranceConfig {
  id: string;
  name: string;
  /** Sprite de la entrada (deco): `entrance<N>` o `dgent_<dungeon>__<archivo>`. */
  entranceSprite: string;
  /** Radio del sector en tiles (el editor lo puede ajustar). */
  defaultRadiusTiles: number;
}

/**
 * Factory de sector de entrada de dungeon: coloca una entrada (deco que se pisa para
 * teletransportarse) sobre una plazoleta de adoquín que —si el builder tiene camino
 * marcado cerca— se conecta con un solo ramal al camino más cercano (`buildCobbleConnection`).
 * Sombra suave (dark source) sobre la entrada. Translado STUB (mismo mapa, baja N tiles)
 * hasta cablear el mapa real del dungeon. El sprite de la entrada se usa directo (lo
 * resuelve `decorationAssets.ts`: `entrance<N>` y `dgent_*`), sin validar contra
 * `ctx.entranceIds` (que solo lista las `dungeon_entrance` genéricas).
 */
function makeDungeonEntranceSector(cfg: DungeonEntranceConfig): SectorDefinition {
  return {
    id: cfg.id,
    name: cfg.name,
    defaultRadiusTiles: cfg.defaultRadiusTiles,
    generate(ctx: SectorContext): SectorOutput {
      const { centerTileX: cx, centerTileY: cy, radiusTiles: R } = ctx;
      const isPath = ctx.isPath ?? ((): boolean => false);
      const decoTiles: TileData[] = [];
      const suppressTiles = new Set<string>();

      // --- Centro EFECTIVO de la entrada: desplazado un poco (seeded, determinista por
      // placement) dentro del sector para que no queden todas centradas/monótonas. El
      // disco de offset deja el claro (radio DGENTRY_CLEAR_RADIUS) dentro del radio R.
      // La plazoleta y el camino de conexión parten de (ex,ey), así que se adaptan. ---
      const rng = createRng(ctx.seed);
      const offMax = Math.max(0, R - DGENTRY_CLEAR_RADIUS);
      const ang = rng.next() * Math.PI * 2;
      const dist = Math.sqrt(rng.next()) * offMax; // sqrt → uniforme en el disco
      // Centro efectivo snapeado al bloque 4×4 → la entrada cae sobre la plazoleta y el
      // camino (que también se snapean) empalma con el camino del builder sin escalón.
      const ex = snapToBlock(cx + Math.round(Math.cos(ang) * dist));
      const ey = snapToBlock(cy + Math.round(Math.sin(ang) * dist));

      // --- Claro alrededor de la entrada: nada de vegetación genérica. ---
      for (let dy = -DGENTRY_CLEAR_RADIUS; dy <= DGENTRY_CLEAR_RADIUS; dy++) {
        for (let dx = -DGENTRY_CLEAR_RADIUS; dx <= DGENTRY_CLEAR_RADIUS; dx++) {
          if (dx * dx + dy * dy > DGENTRY_CLEAR_RADIUS * DGENTRY_CLEAR_RADIUS) continue;
          const tx = ex + dx;
          const ty = ey + dy;
          if (tx < 0 || ty < 0) continue;
          suppressTiles.add(`${tx},${ty}`);
        }
      }

      // --- Adoquín: plazoleta bajo la entrada + camino de conexión al builder. El adoquín
      // esquiva el agua (isWater) → no cruza lagos ni conecta a un camino del otro lado. ---
      const cobble = buildCobbleConnection(ex, ey, isPath, {
        plazaRadius: DGENTRY_PLAZA_RADIUS,
        halfWidth: DGENTRY_ROAD_HALF_WIDTH,
        maxConnect: DGENTRY_MAX_CONNECT,
        isBlocked: ctx.isWater,
      });
      const coastTiles = cobble.coastTiles;
      for (const key of cobble.tiles) suppressTiles.add(key);

      // --- Entrada (deco que ocluye como estructura). NO se bloquea: el player la
      // pisa y el translado se dispara en ese mismo tile. ---
      decoTiles.push({ x: ex, y: ey, sprite: cfg.entranceSprite });

      // Translado MISMO MAPA (placeholder hasta cablear el dungeon real). El `id` usa
      // la celda (cx,cy) → estable por placement aunque el offset cambie el centro.
      const translados: MapTransladoData[] = [
        {
          id: `sector_${cfg.id}_${cx}_${cy}`,
          x: ex,
          y: ey,
          destMapId: ctx.mapId,
          destX: ex,
          destY: ey + DGENTRY_DUNGEON_DROP,
        },
      ];

      // Tenebrosidad: fuente de OSCURIDAD (sombra suave) sobre la entrada, vía el
      // sistema de luces → bordes difuminados. `radius` en PÍXELES.
      const lights: MapLightData[] = [
        {
          id: `sector_${cfg.id}_gloom_${cx}_${cy}`,
          x: ex,
          y: ey,
          radius: Math.round(Math.max(R, DGENTRY_CLEAR_RADIUS) * 0.7 * TILE_PX),
          intensity: DGENTRY_GLOOM_INTENSITY,
          color: '0x05060f',
          dark: true,
        },
      ];

      return { decoTiles, coastTiles, translados, lights, suppressTiles };
    },
  };
}

/** Entrada de catacumbas genérica (placeholder `entrance1`). */
const entradaCatacumbasSector = makeDungeonEntranceSector({
  id: 'entrada_catacumbas',
  name: 'Entrada Catacumbas',
  entranceSprite: 'entrance1',
  defaultRadiusTiles: 12,
});

/**
 * Una "Entrada Dungeon XXX" por cada dungeon con gráficos cargados (8). Cada uno usa
 * su propio sprite de entrada `dgent_<dungeon>__<archivo>` (resuelto por
 * decorationAssets.ts). Donde hay varias variantes se usa la primaria; editable acá.
 */
const DUNGEON_ENTRANCES: DungeonEntranceConfig[] = [
  { id: 'entrada_dungeon_cavoryn', name: 'Entrada Dungeon Cavoryn', entranceSprite: 'dgent_cavoryn__cavoryn_entrance_1', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_crysalis', name: 'Entrada Dungeon Crysalis', entranceSprite: 'dgent_crysalis__crysalis_entrance', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_morassis', name: 'Entrada Dungeon Morassis', entranceSprite: 'dgent_morassis__morassis_entrance', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_pyrion', name: 'Entrada Dungeon Pyrion', entranceSprite: 'dgent_pyrion__pyrion_entrance_1', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_sylvaris', name: 'Entrada Dungeon Sylvaris', entranceSprite: 'dgent_sylvaris__sylvaris_entrance', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_terranor', name: 'Entrada Dungeon Terranor', entranceSprite: 'dgent_terranor__terranor_entrance', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_umbraxis', name: 'Entrada Dungeon Umbraxis', entranceSprite: 'dgent_umbraxis__umbraxis_entrance', defaultRadiusTiles: 12 },
  { id: 'entrada_dungeon_veriil', name: 'Entrada Dungeon Veriil', entranceSprite: 'dgent_veriil__veriil_entrance', defaultRadiusTiles: 12 },
];
const dungeonEntranceSectors = DUNGEON_ENTRANCES.map(makeDungeonEntranceSector);

// ============================================================================
// Sectores de CIUDAD: las dos ciudades de facción (Aresgard roja / Elvaria azul), la
// Ciudad Renegada (Rebeldes, gris) y la Ciudad Principal (neutral, dorada). Cada ciudad:
//   - DESPEJA toda su vegetación (ver WORLD_BUILDER.md),
//   - pone una GRAN plazoleta de adoquín en el centro y se conecta al camino del builder
//     por cada dirección cardinal cercana (mismo adoquín 9-slice, empalme sin costura),
//   - se LLENA con casas de su tipo primario (Aresgard/Elvaria house1 cabaña, Principal
//     house2 piedra, Renegada house3 ladrillo) + 1 house4 de acento, y tira una CALLE de
//     adoquín de cada casa a la plaza,
//   - emite un GLOW de color con la identidad de la facción. OJO: las luces de color solo
//     se renderizan de NOCHE (de día se ve la plaza + casas). La identidad real (área PvP,
//     banner, nombre in-game) se cablea con el sistema de Áreas / assets, no acá. Ver
//     FACTIONS.md y AREAS.md.
// Factory `makeCitySector` para no repetir la lógica entre las 4.
// ============================================================================

/** Fracción del radio que ocupa la plazoleta central de adoquín (0..1). */
const CITY_PLAZA_FRACTION = 0.22;
/** Semi-ancho del camino de conexión de una ciudad: 2 → camino de 5 tiles de ancho. */
const CITY_ROAD_HALF_WIDTH = 2;
/**
 * Distancia máxima (tiles) a la que la ciudad busca el camino del builder. Se mide
 * desde el centro, así que debe superar el radio de la plazoleta para alcanzar un camino
 * pegado a su borde + ~1 celda de puente. Un solo ramal al camino más cercano (no abanico).
 */
const CITY_MAX_CONNECT = 50;
/** Intensidad del glow de color de la facción (solo visible de noche). */
const CITY_GLOW_INTENSITY = 0.35;

/** Separación horizontal (tiles) entre huellas de casas vecinas. */
const CITY_HOUSE_GAP = 1;
/**
 * Separación VERTICAL EXTRA (tiles) entre filas, ADEMÁS del alto visual de la casa y de la
 * avenida de su frente. Chico porque el alto visual (`houseVisualDepthTiles`) ya incluye el
 * techo, que es lo que hacía que las casas se encimaran.
 */
const CITY_HOUSE_GAP_Y = 1;
/** Margen (tiles) desde el borde de la ciudad para las ANCLAS candidatas. */
const CITY_HOUSE_EDGE_MARGIN = 1;
/** Tope de casas por ciudad (el scatter para antes si no hay lugar). */
const CITY_HOUSE_MAX = 12;
/** Casa "acento" que SIEMPRE aparece 1 vez por ciudad (además de las del tipo primario). */
const CITY_ACCENT_HOUSE = 'house4';
/**
 * TODAS las ciudades se generan de **50×50 tiles** (radio 25), sin importar el radio con que
 * se estampó el sector en el builder: se elige la ciudad "normal" y el generador la adapta a
 * 50×50 (más lugar para casas + calles, y uniforme entre las 4).
 */
const CITY_SIZE_RADIUS = 25;
/**
 * Ancho (tiles) de las CALLES INTERNAS de la ciudad (avenidas frente a las casas +
 * conectores a la plaza). 4 = un tile menos que las rutas principales (5). Como es par, se
 * estampa con un pincel asimétrico (offsets `CITY_STREET_LO..CITY_STREET_HI`).
 */
const CITY_STREET_WIDTH = 4;
const CITY_STREET_LO = -Math.floor((CITY_STREET_WIDTH - 1) / 2);
const CITY_STREET_HI = CITY_STREET_LO + CITY_STREET_WIDTH - 1;
/** Máx. filas (≈ `DIRT_MAX_BLOCKS * PATH_BLOCK`) que baja el sendero de tierra buscando el camino. */
const DIRT_MAX_BLOCKS = 10;

/**
 * Sub-tile 1×1 de tierra (`pattern6`) para el sendero puerta→camino, según la COLUMNA del sendero
 * (ancho 4: `[borde, centro, centro, borde]`) y la fila `ty`. A diferencia del surface 4×4 (que
 * sangra 3 tiles), los 1×1 pintan EXACTO su celda. Se usa el nine-slice de `pattern6`:
 *  - col 0 → borde izq (`gridX2`, fade suave a la izquierda),
 *  - col 3 → borde der (`gridX9`, fade a la derecha),
 *  - col 1/2 → centro sólido (`gridX5/6`).
 * Todos en la banda vertical media (`gridY 4..7`, sólida) → SIN borde arriba/abajo. `gy` cicla por
 * `ty mod 4` para variar la textura sin costura. Ver `pattern6_*_1x1` en `assets-index.json`.
 */
function dirtPathSprite(col: number, ty: number): string {
  const gy = 4 + (((ty % 4) + 4) % 4);
  // Bordes con fade CLARO (gx1/gx10 = 22% alpha) para que se distingan del centro sólido
  // (gx5/gx6 = 100%). gx2/gx9 (65%) se veían casi sólidos → parecían "4 de centro".
  const gx = col === 0 ? 1 : col === 3 ? 10 : 4 + col;
  return `pattern6_${gx}_${gy}_1x1`;
}

/** Configuración de una ciudad. */
interface CityConfig {
  id: string;
  name: string;
  defaultRadiusTiles: number;
  /** Color del glow de identidad (hex string, ej. '0xff4444'). */
  glowColor: string;
  /** Si true, dispersa casas alrededor de la plaza. */
  placeHouses: boolean;
  /**
   * Tipo de casa PRIMARIO de la ciudad (`house1` cabaña, `house2` piedra, `house3`
   * ladrillo). Todas las casas son de este tipo salvo UNA `house4` de acento. Cada facción
   * tiene su tipo: Aresgard/Elvaria cabañas, Principal piedra, Renegada ladrillo.
   */
  houseType: string;
}

/**
 * Dispersa casas alrededor de la plaza de una ciudad. Determinista (RNG seedeado por
 * centro). Todas las casas son del tipo PRIMARIO de la ciudad (`houseType`), salvo la
 * PRIMERA colocada que es `house4` de acento (así cada ciudad tiene siempre 1 house4).
 * Coloca hasta `CITY_HOUSE_MAX` casas (≥5 del tipo primario si el espacio alcanza) en una
 * grilla de candidatos mezclada, evitando la plaza/camino de la ciudad (`occupied`), los
 * **caminos del builder** (`isPath`), el borde y otras casas. El chequeo cubre TODO el alto
 * VISIBLE de la casa (del fondo del cuerpo hasta la **fila base**, no solo el rectángulo de
 * colisión) → la casa nunca queda con su parte de abajo sobre un camino (queda "arriba" del
 * camino). La huella bloquea y suprime vegetación. Devuelve también las `anchors` de las
 * casas colocadas para tirarles calles a la plaza.
 */
function scatterCityHouses(
  cx: number,
  cy: number,
  R: number,
  seed: number,
  occupied: Set<string>,
  suppressTiles: Set<string>,
  houseType: string,
  isPath: (tx: number, ty: number) => boolean
): {
  deco: TileData[];
  roof: TileData[];
  blocked: TileData[];
  floor: TileData[];
  anchors: Array<{ ax: number; baseY: number; halfW: number; front: number }>;
  houseCells: Set<string>;
  interiors: MapRect[];
} {
  const deco: TileData[] = [];
  const roof: TileData[] = [];
  const blocked: TileData[] = [];
  const floor: TileData[] = [];
  const anchors: Array<{ ax: number; baseY: number; halfW: number; front: number }> = [];
  const interiors: MapRect[] = [];
  // Unión de los rectángulos visibles de todas las casas → la ciudad los RESTA del adoquín
  // para que ninguna calle quede bajo una casa (la casa queda "arriba" del camino).
  const houseCells = new Set<string>();
  if (HOUSE_IDS.length === 0) return { deco, roof, blocked, floor, anchors, houseCells, interiors };

  const primaryDef = HOUSES[houseType] ?? HOUSES[HOUSE_IDS[0]];
  const accentDef = HOUSES[CITY_ACCENT_HOUSE] ?? primaryDef;

  const rng = createRng(hash2(cx * 7 + 1, cy * 13 + 3, seed));
  // Paso de la grilla derivado de los tipos REALMENTE usados (primario + acento). El paso
  // VERTICAL usa el alto VISUAL (techo incluido) + la avenida del frente + un gap, para que
  // el techo de una casa no se monte sobre la de arriba ni sobre la avenida (no encimar).
  const usedDefs = [primaryDef, accentDef];
  const maxVisual = Math.max(...usedDefs.map((d) => houseVisualDepthTiles(d)));
  const maxWidth = Math.max(...usedDefs.map((d) => houseWidthTiles(d)));
  const stepX = maxWidth + CITY_HOUSE_GAP;
  // Paso vertical = alto visual (techo incluido) + gap. NO se suma la avenida: la avenida de
  // una fila puede quedar debajo del techo de la fila de abajo (queda oculta, el techo va por
  // encima), así que solo hace falta que los rectángulos visuales de las casas no se pisen.
  const stepY = maxVisual + CITY_HOUSE_GAP_Y;
  const limit = R - CITY_HOUSE_EDGE_MARGIN;

  // Candidatos: grilla de anclas dentro del disco de la ciudad (la casa ocupa hacia
  // atrás desde `baseY`, así que dejamos lugar arriba para la profundidad).
  const candidates: Array<{ ax: number; baseY: number }> = [];
  for (let by = cy - limit + maxVisual; by <= cy + limit; by += stepY) {
    for (let ax = cx - limit + maxWidth / 2; ax <= cx + limit; ax += stepX) {
      candidates.push({ ax: Math.round(ax), baseY: Math.round(by) });
    }
  }
  // Mezcla determinista (Fisher-Yates con el RNG seedeado) para que no queden en filas.
  for (let i = candidates.length - 1; i > 0; i--) {
    const j = rng.int(0, i);
    [candidates[i], candidates[j]] = [candidates[j], candidates[i]];
  }

  let placed = 0;
  for (const { ax, baseY } of candidates) {
    if (placed >= CITY_HOUSE_MAX) break;
    // La primera casa que entra es el acento house4; el resto, el tipo primario.
    const def = placed === 0 ? accentDef : primaryDef;

    // Rectángulo VISUAL de la casa: del tope del techo (`baseY - (visualDepth-1)`) hasta la
    // fila BASE (`baseY`), a lo ancho de la huella. Usa el alto VISUAL (techo incluido), no la
    // huella de colisión, para que el chequeo/ocupación impidan que dos casas (o el techo de
    // una y el cuerpo de otra) se encimen, y que la base no caiga sobre un camino.
    const depth = houseVisualDepthTiles(def);
    const halfW = def.collision?.halfWidth ?? Math.floor(houseWidthTiles(def) / 2);
    const left = ax - halfW;
    const right = ax + halfW;
    const topRow = baseY - (depth - 1);

    // El CENTRO de la casa debe caer dentro del disco de la ciudad (las esquinas pueden
    // asomar un poco); usamos el centro y no cada tile para empaquetar todo el anillo sin
    // rechazar casas del borde (clave en ciudades chicas para llegar a ≥5).
    const centerY = baseY - Math.floor(depth / 2);
    if ((ax - cx) ** 2 + (centerY - cy) ** 2 > R * R) continue;

    // El rectángulo visual no debe salirse del mundo, ni pisar plaza/calle de la ciudad
    // (`occupied`), ni un camino del builder (`isPath`), ni otra casa.
    const cells: string[] = [];
    let ok = true;
    for (let ty = topRow; ty <= baseY && ok; ty++) {
      for (let tx = left; tx <= right; tx++) {
        if (tx < 0 || ty < 0 || occupied.has(`${tx},${ty}`) || isPath(tx, ty)) {
          ok = false;
          break;
        }
        cells.push(`${tx},${ty}`);
      }
    }
    if (!ok) continue;

    const stamp = stampHouse(ax, baseY, def);
    deco.push(...stamp.deco);
    roof.push(...stamp.roof);
    blocked.push(...stamp.blocked);
    floor.push(...stamp.floor);
    interiors.push(stamp.interiorRect);
    // Ocupar/suprimir TODO el rectángulo VISUAL (techo incluido) → separa casas (no encimar)
    // y limpia vegetación.
    for (const key of cells) {
      occupied.add(key);
      suppressTiles.add(key);
    }
    // `houseCells` (lo que se RESTA del adoquín) es solo el CUERPO (huella), NO el techo: así
    // la avenida de la casa de arriba NO se come contra el techo alto de la de abajo, y solo
    // se saca el camino donde realmente están las paredes. El techo va por encima igual.
    const bodyTop = baseY - (houseFootprintDepthTiles(def) - 1);
    for (let ty = bodyTop; ty <= baseY; ty++) {
      for (let tx = left; tx <= right; tx++) houseCells.add(`${tx},${ty}`);
    }
    // `front` = fila de la pared de adelante (`baseY - collision.front`). El piso interior llega
    // hasta `front+1`; el sendero arranca en `front+2` para NO pisarlo.
    anchors.push({ ax, baseY, halfW, front: baseY - (def.collision?.front ?? 0) });
    placed++;
  }

  return { deco, roof, blocked, floor, anchors, houseCells, interiors };
}

function makeCitySector(cfg: CityConfig): SectorDefinition {
  return {
    id: cfg.id,
    name: cfg.name,
    defaultRadiusTiles: cfg.defaultRadiusTiles,
    generate(ctx: SectorContext): SectorOutput {
      const { centerTileX: cx, centerTileY: cy, seed } = ctx;
      // TODAS las ciudades se generan de 50×50 (radio 25), sin importar el radio del builder.
      const R = CITY_SIZE_RADIUS;
      const isPath = ctx.isPath ?? ((): boolean => false);
      const suppressTiles = new Set<string>();

      // Despejar TODA la vegetación genérica dentro del círculo de la ciudad.
      for (let dy = -R; dy <= R; dy++) {
        for (let dx = -R; dx <= R; dx++) {
          if (dx * dx + dy * dy > R * R) continue;
          const tx = cx + dx;
          const ty = cy + dy;
          if (tx < 0 || ty < 0) continue;
          suppressTiles.add(`${tx},${ty}`);
        }
      }

      // Road set CRUDO: gran plazoleta central + conexión al builder por cardinal. Se arma
      // sin 9-slice para poder AGREGARLE las calles internas antes de resolver el sprite.
      const roadSet = computeCobbleRoadSet(cx, cy, isPath, {
        plazaRadius: Math.max(4, Math.round(R * CITY_PLAZA_FRACTION)),
        halfWidth: CITY_ROAD_HALF_WIDTH,
        maxConnect: CITY_MAX_CONNECT,
        isBlocked: ctx.isWater,
      });

      // Casas del tipo de la ciudad (+1 house4 de acento) alrededor de la plaza. No se
      // colocan sobre la plaza/camino.
      let houseDeco: TileData[] = [];
      let houseRoof: TileData[] = [];
      let houseBlocked: TileData[] = [];
      let houseFloor: TileData[] = [];
      let houseAreas: MapAreaData[] = [];
      const cityDirt: TileData[] = [];
      if (cfg.placeHouses) {
        const occupied = new Set<string>(roadSet);
        const houses = scatterCityHouses(cx, cy, R, seed, occupied, suppressTiles, cfg.houseType, isPath);
        houseDeco = houses.deco;
        houseRoof = houses.roof;
        houseBlocked = houses.blocked;
        houseFloor = houses.floor;
        // Un área `interior` por casa: al entrar, oscurece afuera + atenúa el techo. Id
        // estable por ciudad+índice. Ver docs/AREAS.md y LightingSystem.
        houseAreas = houses.interiors.map((rect, i) => ({
          id: `house_interior_${cfg.id}_${cx}_${cy}_${i}`,
          rects: [rect],
          interior: true,
        }));

        // Avenidas + conectores (calles internas de ancho CITY_STREET_WIDTH = 4). Por casa:
        //  1) Una AVENIDA de adoquín de ANCHO COMPLETO de la casa, pegada al frente (sur) de
        //     la base (filas `baseY+1 .. baseY+CITY_STREET_WIDTH`) → la casa queda flush con la
        //     calle, sin pasto. Las avenidas de casas de la misma fila se fusionan solas.
        //  2) Un CONECTOR de 4 de ancho de la avenida a la plaza, "L" horizontal-primero (el
        //     vertical baja por la columna de la plaza, no cruza casas → conecta todas).
        // Todo va al MISMO roadSet → se 9-slicea junto con la plaza (empalme sin costura).
        const scx = snapToBlock(cx);
        const scy = snapToBlock(cy);
        for (const h of houses.anchors) {
          for (let ty = h.baseY + 1; ty <= h.baseY + CITY_STREET_WIDTH; ty++) {
            for (let tx = h.ax - h.halfW; tx <= h.ax + h.halfW; tx++) {
              if (tx >= 0 && ty >= 0) roadSet.add(`${tx},${ty}`);
            }
          }
          // Fila de arranque del conector: dentro de la banda de la avenida (baseY+1..+4).
          const avenueMidY = h.baseY + 1 - CITY_STREET_LO;
          stampBandL(roadSet, snapToBlock(h.ax), avenueMidY, scx, scy, CITY_STREET_LO, CITY_STREET_HI);
        }

        // Sendero de TIERRA (`pattern6`, sub-tiles 1×1) de la PUERTA de cada casa al camino:
        // baja desde `front+2` (justo bajo el piso interior) tile por tile, al ancho de la puerta
        // (ax..ax+3), hasta el PRIMER camino que encuentra (su propia avenida flush enfrente,
        // plaza o ruta). Se dibuja DEBAJO del adoquín (capa `layer2_floor_objects`); donde hay
        // calle, la tierra queda oculta → visible sólo el umbral corto puerta→calle.
        //
        // CLAVE: la avenida del frente (`baseY+1..+CITY_STREET_WIDTH`) SIEMPRE está flush contra la
        // casa, así que es el primer camino y el sendero corta ahí → un umbral corto (~2 tiles), NO
        // un puente largo. Antes se SALTEABA la avenida en la detección y el sendero seguía hasta la
        // plaza/ruta lejana, tendiendo tiras de tierra de 5-8 tiles que cruzaban el camino. Igual se
        // frena antes de entrar en la huella de OTRA casa (por si una casa quedara sin avenida flush
        // y tuviera otra casa debajo); `houseArea` = celdas ocupadas por casas (cuerpo + piso), y
        // sólo se chequea debajo de la propia casa (`ty > h.baseY`), así nunca frena sobre sí misma.
        const houseArea = new Set<string>(houses.houseCells);
        for (const t of houses.floor) houseArea.add(`${t.x},${t.y}`);
        for (const h of houses.anchors) {
          const xLeft = h.ax; // puerta default = offsets +1,+2 → ax..ax+3 (puerta + 1 de margen)
          const xRight = h.ax + 3;
          const startY = h.front + 2; // justo DEBAJO del piso interior (que llega a front+1) → no lo pisa
          const maxY = startY + DIRT_MAX_BLOCKS * PATH_BLOCK;
          for (let ty = startY; ty <= maxY; ty++) {
            // Frenar ANTES de meterse en otra casa (debajo de la propia) sin pintar esa fila.
            if (ty > h.baseY) {
              let intoHouse = false;
              for (let tx = xLeft; tx <= xRight; tx++) {
                if (houseArea.has(`${tx},${ty}`)) {
                  intoHouse = true;
                  break;
                }
              }
              if (intoHouse) break;
            }
            // Pintar la fila de tierra (bajo el adoquín si es calle → oculta).
            for (let tx = xLeft; tx <= xRight; tx++) {
              if (tx >= 0 && ty >= 0) {
                cityDirt.push({ x: tx, y: ty, sprite: dirtPathSprite(tx - xLeft, ty) });
                suppressTiles.add(`${tx},${ty}`);
              }
            }
            // Llegó a un camino (típicamente la avenida flush del frente) → cortar el sendero acá.
            let rowIsRoad = false;
            for (let tx = xLeft; tx <= xRight; tx++) {
              if (roadSet.has(`${tx},${ty}`) || isPath(tx, ty)) {
                rowIsRoad = true;
                break;
              }
            }
            if (rowIsRoad) break;
          }
        }

        // La casa va SIEMPRE arriba del camino: restamos el rectángulo VISUAL de cada casa
        // (techo incluido) del adoquín, así ninguna calle queda bajo la casa ni sobre su base
        // — la avenida muere flush en `baseY+1`. (Los tiles de casa nunca son camino.)
        for (const key of houses.houseCells) roadSet.delete(key);
      }

      // Todo el adoquín (plaza + conexiones + calles) suprime vegetación y se 9-slicea junto.
      for (const key of roadSet) suppressTiles.add(key);
      const coastTiles = sliceRoadTiles(roadSet, isPath);

      // Glow de color de la facción (cosmético; solo de noche). `radius` en PÍXELES.
      const lights: MapLightData[] = [
        {
          id: `sector_${cfg.id}_glow_${cx}_${cy}`,
          x: cx,
          y: cy,
          radius: Math.round(R * TILE_PX),
          intensity: CITY_GLOW_INTENSITY,
          color: cfg.glowColor,
        },
      ];

      return {
        coastTiles,
        dirtTiles: cityDirt.length > 0 ? cityDirt : undefined,
        // Piso interior de las casas → capa `layer1_ground` (override sobre el pasto/agua base).
        groundTiles: houseFloor.length > 0 ? houseFloor : undefined,
        decoTiles: houseDeco.length > 0 ? houseDeco : undefined,
        roofTiles: houseRoof.length > 0 ? houseRoof : undefined,
        blockedTiles: houseBlocked.length > 0 ? houseBlocked : undefined,
        areas: houseAreas.length > 0 ? houseAreas : undefined,
        lights,
        suppressTiles,
      };
    },
  };
}

/** Ciudad de facción Aresgard (roja). Cabañas (house1). */
const ciudadAresgardSector = makeCitySector({
  id: 'ciudad_aresgard',
  name: 'Ciudad Aresgard',
  defaultRadiusTiles: 24,
  glowColor: '0xff4444',
  placeHouses: true,
  houseType: 'house1',
});

/** Ciudad de facción Elvaria (azul). Cabañas (house1). */
const ciudadElvariaSector = makeCitySector({
  id: 'ciudad_elvaria',
  name: 'Ciudad Elvaria',
  defaultRadiusTiles: 24,
  glowColor: '0x4488ff',
  placeHouses: true,
  houseType: 'house1',
});

/** Ciudad Renegada (Rebeldes, gris). Ladrillo (house3). */
const ciudadRenegadaSector = makeCitySector({
  id: 'ciudad_renegada',
  name: 'Ciudad Renegada',
  defaultRadiusTiles: 20,
  glowColor: '0x8890a0',
  placeHouses: true,
  houseType: 'house3',
});

/** Ciudad Principal (neutral, dorada). La más grande. Piedra (house2). */
const ciudadPrincipalSector = makeCitySector({
  id: 'ciudad_principal',
  name: 'Ciudad Principal',
  defaultRadiusTiles: 28,
  glowColor: '0xffcc55',
  placeHouses: true,
  houseType: 'house2',
});

/** Registro de sectores disponibles. Agregar nuevos acá. */
export const SECTORS: Record<string, SectorDefinition> = {
  [pruebaSector.id]: pruebaSector,
  [entradaCatacumbasSector.id]: entradaCatacumbasSector,
  ...Object.fromEntries(dungeonEntranceSectors.map((s) => [s.id, s])),
  [ciudadAresgardSector.id]: ciudadAresgardSector,
  [ciudadElvariaSector.id]: ciudadElvariaSector,
  [ciudadRenegadaSector.id]: ciudadRenegadaSector,
  [ciudadPrincipalSector.id]: ciudadPrincipalSector,
};

export function getSector(id: string): SectorDefinition | undefined {
  return SECTORS[id];
}

export function listSectors(): SectorDefinition[] {
  return Object.values(SECTORS);
}
